import PropTypes from 'prop-types';
import React from 'react';

import { useIntl } from 'react-intl';
import BooleanSelect from '~/ui/components/BooleanSelect/BooleanSelect';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Icon from '~/ui/components/Icon/Icon';
import Text from '~/ui/components/Text/Text';
import { createConditionRule } from '~/utils/workflow';

import ConditionSelect from './ConditionSelect';

function ConditionRuleInput({
  conditionRule,
  id,
  label = undefined,
  onChange,
}) {
  const intl = useIntl();
  const hasConditionRule = !!conditionRule;

  const defaultLabel = intl.formatMessage({
    id: 'ConditionRuleInput.showWhen',
    defaultMessage: 'Show when',
    description: 'Default label for condition rule input',
  });

  function onSpecifyCondition() {
    hasConditionRule ? onChange(null) : onChange(createConditionRule());
  }

  return (
    <FlexLayout flexDirection="column" space={3}>
      <FlexLayout
        alignItems="center"
        id={id}
        space={2}
        onClick={hasConditionRule ? null : onSpecifyCondition}
      >
        <Icon
          color={hasConditionRule ? 'gray-700' : 'blue-500'}
          icon="condition"
          size="s"
        />
        <Text
          color={hasConditionRule ? 'gray-700' : 'blue-500'}
          variant="xs-dense-bold"
        >
          {hasConditionRule
            ? intl.formatMessage({
                id: 'ConditionRuleInput.condition',
                defaultMessage: 'Condition',
                description: 'Label when condition is set',
              })
            : intl.formatMessage({
                id: 'ConditionRuleInput.specifyCondition',
                defaultMessage: 'Specify Condition',
                description: 'Label when no condition is set',
              })}
        </Text>
      </FlexLayout>
      {hasConditionRule && (
        <FlexLayout
          alignItems="center"
          justifyContent="space-between"
          space={4}
        >
          <FlexLayout alignItems="center" space={4}>
            <Text color="black" variant="xs-dense">
              {label ?? defaultLabel}
            </Text>
            <ConditionSelect
              width="l"
              value={conditionRule.id}
              onChange={(id) => onChange({ ...conditionRule, id })}
            />
            <BooleanSelect
              width="input.s.width"
              placeholder={intl.formatMessage({
                id: 'ConditionRuleInput.booleanPlaceholder',
                defaultMessage: 'is true / is false',
                description: 'Placeholder for boolean condition selection',
              })}
              value={conditionRule.satisfiedWhenCondMet}
              onChange={(value) =>
                onChange({ ...conditionRule, satisfiedWhenCondMet: value })
              }
            />
          </FlexLayout>
          <Icon
            color="black-alpha-75"
            icon="trash"
            size="s"
            title={intl.formatMessage({
              id: 'ConditionRuleInput.removeCondition',
              defaultMessage: 'remove condition',
              description: 'Tooltip for remove condition button',
            })}
            onClick={onSpecifyCondition}
          />
        </FlexLayout>
      )}
    </FlexLayout>
  );
}

export const conditionRulePropType = PropTypes.shape({
  id: PropTypes.string,
  satisfiedWhenCondMet: PropTypes.bool,
  enabledWhenSatisfied: PropTypes.bool,
});
ConditionRuleInput.propTypes = {
  /** instance of ConditionRule */
  conditionRule: conditionRulePropType,
  /** Optional ID */
  id: PropTypes.string,
  /** Optional text label */
  label: PropTypes.string,
  /** callback function returning an instance of ConditionRule */
  onChange: PropTypes.func.isRequired,
};

export default ConditionRuleInput;
